import { all, takeLatest, put, call, select } from 'redux-saga/effects';

import { Api } from '$api';
import { toastActions } from '$store/toast';
import { offersActions, offersSelector } from '$store/offers/index';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import { OffersEditOfferAction, OffersLoadOfferAction } from '$store/offers/interface';
import { AttachItem } from '$store/models';
import { RootState } from '$store/rootReducer';
import { attachItemsSelector } from '$store/attach';
import { t } from '$translation';
import router from '$navigation/router';
import { VIEW_OFFER } from '$shared/constants/pages';
import { goBack } from '$navigation/helper';

function* loadOffersWorker() {
  try {
    const data = yield call(Api.get, '/offers');
    yield put(
      batchActions(
        offersActions.setOffers(data.offers.map((offer) => offer.id)),
        offersActions.setMyOffers(data.myOffers.map((offer) => offer.id)),
        offersActions.setOffersInfo([
          ...data.offers,
          ...data.myOffers,
          ...data.statsOffers,
        ]),
        offersActions.setStats(data.stats),
        offersActions.setOffersLoadingStatus(''),
        mainActions.setUsers(data.users),
      ),
    );
  } catch (e) {
    yield put(
      batchActions(
        toastActions.setToastFail(e.message),
        offersActions.setOffersLoadingStatus('failed'),
      ),
    );
  }
}

function* loadOfferWorker(action: OffersLoadOfferAction) {
  try {
    const data = yield call(Api.get, `/offers/${action.payload}`);
    yield put(
      batchActions(
        offersActions.setOffersInfo([data.offer]),
        offersActions.setOfferLoadingStatus(''),
        mainActions.setUser(data.user),
      ),
    );
  } catch (e) {
    yield put(
      batchActions(
        toastActions.setToastFail(e.message),
        offersActions.setOfferLoadingStatus('failed'),
      ),
    );
  }
}

function* createOfferWorker() {
  try {
    yield put(toastActions.setToastLoading());
    const { create } = yield select(offersSelector);
    const content: AttachItem[] | 'photo_uploading' = yield select((state: RootState) =>
      attachItemsSelector(state, `offer`),
    );

    if (content === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    const data = yield call(Api.post, '/offers/create', {
      description: create.description,
      sharePercent: +create.percent,
      shareType: +create.type,
      periodDays: +create.periodDays,
      content,
    });
    yield put(toastActions.setToastSuccess());
    yield call(
      // @ts-ignore
      router.navigate,
      VIEW_OFFER,
      {
        id: data.offerId,
      },
      {
        replace: true,
      },
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* editOfferWorker(action: OffersEditOfferAction) {
  try {
    yield put(toastActions.setToastLoading());
    const { edit } = yield select(offersSelector);
    const content: AttachItem[] | 'photo_uploading' = yield select((state: RootState) =>
      attachItemsSelector(state, `edit_offer`),
    );

    if (content === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    const data = yield call(Api.post, `/offers/${action.payload}/edit`, {
      description: edit.description,
      content,
    });
    yield put(
      batchActions(
        toastActions.setToastSuccess(),
        offersActions.setOffersInfo([data.offer]),
      ),
    );
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* offersSaga() {
  yield all([
    takeLatest(offersActions.loadOffers, loadOffersWorker),
    takeLatest(offersActions.loadOffer, loadOfferWorker),
    takeLatest(offersActions.createOffer, createOfferWorker),
    takeLatest(offersActions.editOffer, editOfferWorker),
  ]);
}
